<template>
  <model-form
    ref="modelForm"
    :model="supportArticle"
    @change="$emit('change')"
  >
    <div class="q-gutter-sm">
      <div class="row q-col-gutter-md items-start q-mb-md">
        <q-input
          v-model="supportArticle.title"
          class="col-4"
          label="Title"
          bottom-slots
          :rules="[val => !!val || 'Field is required']"
        />
        <q-input
          v-model="supportArticle.description"
          class="col-4"
          label="Description"
          bottom-slots
          :rules="[val => !!val || 'Field is required']"
        />
        <image-uploader
          v-model="supportArticle.image"
          class="col-4"
          :image-url="supportArticle.imageUrl"
          :model="supportArticle"
        />
      </div>
      <div class="row q-col-gutter-md items-center">
        <q-checkbox
          v-model="supportArticle.adminRelated"
          class="col-3"
          label="Admin"
        />
        <q-checkbox
          v-model="supportArticle.posRelated"
          class="col-3"
          label="Point of Sale"
        />
        <q-checkbox
          v-model="supportArticle.indyOnly"
          class="col-3"
          color="negative"
          label="INDY Only"
        >
          <q-tooltip>
            Only show article to INDY Super Users
          </q-tooltip>
        </q-checkbox>
        <q-checkbox
          v-model="supportArticle.published"
          class="col"
          label="Published"
        />
      </div>
      <div class="row q-col-gutter-md items-start q-mt-lg">
        <q-select
          v-model="supportArticle.type"
          class="col-3"
          label="Type"
          :options="supportArticle.fieldOptions('type')"
          emit-value
          map-options
          bottom-slots
        />
        <template v-if="supportArticle.type === 'Video'">
          <q-select
            v-model="supportArticle.videoType"
            class="col-3"
            label="Video Type"
            :options="supportArticle.fieldOptions('videoType')"
            emit-value
            map-options
            bottom-slots
          />
          <q-input
            v-model="supportArticle.video"
            class="col-6"
            :label="supportArticle.videoType + ' Video ID'"
            bottom-slots
          />
        </template>
      </div>
      <template v-if="supportArticle.type === 'Article'">
        <div class="row items-start">
          <q-editor
            v-model="supportArticle.content"
            class="internal-documentation"
            :dense="$q.screen.lt.md"
            :definitions="{
              insert_img: {
                tip: 'Insert Image',
                icon: 'photo',
                handler: insertImage,
                disable: insertImageIsDisabled
              }
            }"
            :toolbar="[
              [
                {
                  label: $q.lang.editor.align,
                  icon: $q.iconSet.editor.align,
                  fixedLabel: true,
                  list: 'only-icons',
                  options: ['left', 'center', 'right', 'justify']
                }
              ],
              ['insert_img'],
              ['bold', 'italic', 'strike', 'underline', 'subscript', 'superscript'],
              ['token', 'hr', 'link', 'custom_btn'],
              ['print', 'fullscreen'],
              [
                {
                  label: $q.lang.editor.formatting,
                  icon: $q.iconSet.editor.formatting,
                  list: 'no-icons',
                  options: [
                    'p',
                    'h1',
                    'h2',
                    'h3',
                    'h4',
                    'h5',
                    'h6',
                    'code'
                  ]
                },
                {
                  label: $q.lang.editor.fontSize,
                  icon: $q.iconSet.editor.fontSize,
                  fixedLabel: true,
                  fixedIcon: true,
                  list: 'no-icons',
                  options: [
                    'size-1',
                    'size-2',
                    'size-3',
                    'size-4',
                    'size-5',
                    'size-6',
                    'size-7'
                  ]
                },
                {
                  label: $q.lang.editor.defaultFont,
                  icon: $q.iconSet.editor.font,
                  fixedIcon: true,
                  list: 'no-icons',
                  options: [
                    'default_font',
                    'arial',
                    'arial_black',
                    'comic_sans',
                    'courier_new',
                    'impact',
                    'lucida_grande',
                    'times_new_roman',
                    'verdana'
                  ]
                },
                'removeFormat'
              ],
              ['quote', 'unordered', 'ordered', 'outdent', 'indent'],

              ['undo', 'redo'],
              ['viewsource']
            ]"
            :fonts="{
              arial: 'Arial',
              arial_black: 'Arial Black',
              comic_sans: 'Comic Sans MS',
              courier_new: 'Courier New',
              impact: 'Impact',
              lucida_grande: 'Lucida Grande',
              times_new_roman: 'Times New Roman',
              verdana: 'Verdana'
            }"
          />
        </div>
      </template>
      <template v-if="supportArticle.type === 'Video'">
        <div class="row">
          <div class="col">
            <q-video
              v-if="supportArticle.type === 'YouTube'"
              style="width: 100%"
              :ratio="16/9"
              :src="'https://www.youtube.com/embed/' + supportArticle.video"
            />
            <q-video
              v-if="supportArticle.type === 'Loom'"
              style="width: 100%"
              :ratio="16/9"
              :src="'https://www.loom.com/embed/' + supportArticle.video"
            />
          </div>
        </div>
      </template>
    </div>
  </model-form>
</template>

<script>

import { imgixHost } from 'src/config'
import ImageUploader from 'src/modules/admin/components/ImageUploader.vue'
import ModelForm from 'src/modules/admin/components/ModelForm/ModelForm'

export default {
  name: 'SupportArticleForm',
  components: {
    ModelForm,
    ImageUploader
  },
  models: {
    supportArticle: 'default'
  },
  emits: ['change'],
  methods: {
    insertImage () {
      const selectedNode = window.getSelection().baseNode
      if (selectedNode.parentNode.closest('.q-editor__content') || selectedNode.closest('.q-editor__content')) {
        // create an input file element to open file dialog
        const input = document.createElement('input')
        input.type = 'file'
        input.accept = 'image/*'
        input.onchange = async _ => {
          const imageLoading = document.createElement('div')
          imageLoading.textContent = 'Image Loading...'
          selectedNode.append(imageLoading)
          const files = Array.from(input.files)
          const file = files[0]
          const { key } = await this.$directUpload.uploadFile(file)
          const imageElement = document.createElement('img')
          imageElement.src = imgixHost + '/' + key + '?max-w=1000&fit=fill&auto=format,compress'
          imageElement.style = 'max-width: 100%;'
          selectedNode.append(imageElement)
          imageLoading.remove()
          const content = selectedNode.parentNode.closest('.q-editor__content') || selectedNode.closest('.q-editor__content')
          this.supportArticle.content = content.innerHTML.toString()
        }
        input.click()
      }
    },
    insertImageIsDisabled () {
      const selectedNode = window.getSelection().baseNode
      return typeof selectedNode.append !== 'function'
    },
    show (model, initialData) {
      this.$refs.modelForm.show(model, initialData)
    }
  }
}

</script>

<style lang="scss" scoped>
  .banner-img {
    width: 100%;
  }
</style>
